<template>
  <div id="committee-members-list">
    <!-- ===== HEADER CARD ===== -->
    <v-card class="header-card mb-6">
      <div class="header-backdrop-effect"></div>
      <v-row class="header-gradient header-content pa-8" no-gutters>
        <v-col cols="12" sm="8" class="d-flex align-center gap-4">
          <div class="header-icon-box">
            <v-icon size="48" color="white">mdi-account-multiple-plus</v-icon>
          </div>
          <div>
            <h1 class="header-title">Phase 1 - {{ PageTitle }}</h1>
            <p class="header-subtitle">Build your core team - "The Visionary Four"</p>
          </div>
        </v-col>
        <v-col cols="12" sm="4" class="d-flex justify-end align-center gap-2">
          <v-btn
            v-if="TableOptions1.CreateFlag"
            class="action-btn add-member-btn"
            @click.prevent="addRecordPrompt = true"
          >
            <v-icon left>mdi-plus-circle</v-icon>
            Add Member
          </v-btn>
          <v-btn class="action-btn" @click.prevent="refreshPageData">
            <v-icon left>mdi-refresh</v-icon>
            Refresh
          </v-btn>
          <router-link to="/mission-one-lakh/dashboard">
            <v-btn class="action-btn">
              <v-icon left>mdi-arrow-left</v-icon>
              Back
            </v-btn>
          </router-link>
        </v-col>
      </v-row>
    </v-card>

    <!-- ===== INFO CARD ===== -->
    <v-card class="info-card mb-6 pa-6">
      <v-row>
        <v-col cols="12" sm="6">
          <div class="info-item">
            <v-icon color="#0097d7" size="24">mdi-calendar-clock</v-icon>
            <div class="ml-3">
              <span class="info-label">Deadline Date</span>
              <p class="info-value">{{ MolSettingValues.DeadLineDateTxt }}</p>
            </div>
          </div>
        </v-col>
        <v-col cols="12" sm="6">
          <div class="info-item">
            <v-icon color="#0097d7" size="24">mdi-account-group</v-icon>
            <div class="ml-3">
              <span class="info-label">Members Required</span>
              <p class="info-value">Min: {{ MolSettingValues.MinimumCount }} | Max: {{ MolSettingValues.MaximumCount }}</p>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-card>

    <!-- ===== INSTRUCTIONS CARD ===== -->
    <v-card class="instructions-card mb-6 pa-8">
      <h2 class="instruction-heading">
        <v-icon left color="#ff9800">mdi-lightbulb-on</v-icon>
        Key Information
      </h2>
      <div class="instruction-content">
        <div class="instruction-paragraph">
          <v-icon color="#4caf50">mdi-check-circle</v-icon>
          <span>The first step is to build your core team. Select four dedicated members to serve as your <strong>"The Visionary Four"</strong>.</span>
        </div>
        <div class="instruction-paragraph">
          <v-icon color="#4caf50">mdi-check-circle</v-icon>
          <span><strong>Action:</strong> Appoint these 4 members and update their names on the official JCI India portal.</span>
        </div>
        <div class="instruction-paragraph">
          <v-icon color="#4caf50">mdi-check-circle</v-icon>
          <span><strong>Reward:</strong> Instant recognition for your LO as soon as the names are live!</span>
        </div>

        <div class="requirement-box mt-6">
          <v-icon color="#ff9800" size="28">mdi-alert-circle</v-icon>
          <div>
            <p class="mb-3">Important Notes:</p>
            <ul>
              <li>The first member you add will be considered the <strong>Committee Chairman</strong></li>
              <li>Minimum {{ MolSettingValues.MinimumCount }} members are required to form a committee</li>
              <li>Maximum {{ MolSettingValues.MaximumCount }} members allowed in a committee</li>
              <li>Start by clicking the "Add Member" button above</li>
            </ul>
          </div>
        </div>
      </div>
    </v-card>

    <!-- ===== LOADING STATE ===== -->
    <v-overlay v-model="OverlayLoadingFlag" contained class="align-center justify-center">
      <v-progress-circular indeterminate size="64" color="#0097d7"></v-progress-circular>
    </v-overlay>

    <!-- ===== SKELETON LOADING ===== -->
    <div v-if="StartupLoadingFlag">
      <v-row>
        <v-col cols="12" sm="6" md="4" v-for="n in 3" :key="n">
          <v-skeleton-loader type="card-avatar"></v-skeleton-loader>
        </v-col>
      </v-row>
    </div>

    <!-- ===== TABLE/MEMBERS CARD ===== -->
    <v-card class="table-card mb-6 pa-8" v-if="rows.length > 0">
      <h2 class="table-title mb-8">
        <v-icon color="#0097d7">mdi-account-multiple</v-icon>
        {{ rows.length }} Committee Members Found
      </h2>

      <v-row>
        <v-col
          v-for="item in rows"
          :key="item.MemberId"
          cols="12"
          sm="6"
          md="4"
          lg="4"
        >
          <v-card class="member-card">
            <!-- Member Header with Avatar -->
            <div class="member-header">
              <v-avatar size="140">
                <img :src="item.ProfilePic" :alt="item.MemberName" />
              </v-avatar>
            </div>

            <!-- Member Info -->
            <div class="member-info">
              <h3 class="member-name">{{ item.MemberNameTxt }}</h3>

              <div class="member-designation">
                <v-chip
                  :color="getDesignationColor(item.CommitteeMemberDesignation)"
                  text-color="white"
                  size="small"
                >
                  {{ item.CommitteeMemberDesignation }}
                </v-chip>
              </div>

              <div class="member-details">
                <div class="detail-row" v-if="item.MobileNumber">
                  <v-icon size="18" color="#0097d7">mdi-phone</v-icon>
                  <span class="detail-text">{{ item.MobileNumber }}</span>
                </div>
                <div class="detail-row" v-if="item.Email">
                  <v-icon size="18" color="#0097d7">mdi-email</v-icon>
                  <span class="detail-text">{{ item.Email }}</span>
                </div>
              </div>
            </div>

            <!-- Member Actions -->
            <v-card-actions class="member-actions" v-if="item.DeleteFlag">
              <v-spacer></v-spacer>
              <v-btn
                @click="deleteData(item)"
                color="#e74c3c"
                text
                size="small"
              >
                <v-icon left>mdi-delete</v-icon>
                Delete
              </v-btn>
              <v-spacer></v-spacer>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-card>

    <!-- ===== EMPTY STATE ===== -->
    <v-card
      class="empty-state-card pa-12 text-center"
      v-if="!StartupLoadingFlag && rows.length === 0 && !SearchFlag"
    >
      <v-icon size="100" color="#bdbdbd" class="mb-6">mdi-inbox-outline</v-icon>
      <h2 class="mb-4" style="color: #757575;">No members yet</h2>
      <p class="mb-8" style="color: #999;">
        Start building your committee by adding members. Click the "Add Member" button above to get started!
      </p>
      <v-btn
        v-if="TableOptions1.CreateFlag"
        @click.prevent="addRecordPrompt = true"
        color="#0097d7"
        large
        rounded
        style="font-weight: 900; font-size: 14px; letter-spacing: 0.5px;"
      >
        <v-icon left>mdi-plus-circle</v-icon>
        Add First Member
      </v-btn>
    </v-card>

    <!-- ===== ADD MEMBER DIALOG ===== -->
    <v-dialog v-model="addRecordPrompt" persistent max-width="700px">
      <committee-member-create
        :addRecordPrompt="addRecordPrompt"
        @hideAddRecordPrompt="hideAddRecordPrompt"
        :LomCode="LomCode"
        v-if="addRecordPrompt"
      ></committee-member-create>
    </v-dialog>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import CommitteeMemberCreate from "@/view/pages/erp/lom/mission-onelakh/CommitteeMemberCreate.vue";
import Swal from "sweetalert2";

export default {
  mixins: [common],
  components: {
    CommitteeMemberCreate,
  },
  data() {
    return {
      valid1: true,
      valid2: true,
      LoadingFlag: false,
      StartupLoadingFlag: false,
      SearchFlag: false,
      SubmitFlag: false,
      ResultFlag: false,
      CreateFlag: false,
      OverlayLoadingFlag: false,
      rows: [],
      TableOptions1: {
        CreateFlag: false,
      },
      TableColumns1: [],
      selected: [],
      search: "",
      LomCode: "",
      addRecordPrompt: false,
      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      MolSettingValues: [],
    };
  },
  mounted() {},
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;

        var CurrentJciYearId = parseInt(tr.CurrentJciYearId);
        console.log({ CurrentJciYearId });
        this.JciYearCode = CurrentJciYearId;

        var YearName = parseInt(tr.CurrentJciYearName);
        console.log({ YearName });
        this.YearName = YearName;

        var NextJciYearId = parseInt(tr.NextJciYearId);
        console.log({ NextJciYearId });
        this.NextYearCode = NextJciYearId;

        var NextJciYearName = parseInt(tr.NextJciYearName);
        console.log({ NextJciYearName });
        this.NextYearName = NextJciYearName;

        this.CurrentYearId = CurrentJciYearId;

        var LomId = this.$session.get("LomId");
        LomId = LomId == (null || undefined) ? "" : LomId;
        this.LomCode = LomId;

        var ZoneCode = this.$session.get("ZoneId");
        ZoneCode = ZoneCode == (null || undefined) ? "" : ZoneCode;
        console.log("ZoneCode=" + ZoneCode);
        this.ZoneCode = ZoneCode;

        this.pageData();
      }
    },
  },
  methods: {
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;
      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "mol_committee",
        Action: "list",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
    },
    pageData() {
      console.log("pageData called");
      this.StartupLoadingFlag = true;
      this.getMolSetting();
      this.getTableRecords();
    },
    validate() {
      if (this.$refs.form2.validate()) {
        this.snackbar = true;
      }
    },
    hideAddRecordPrompt(flag) {
      console.log("hideAddRecordPrompt called");
      this.addRecordPrompt = false;
      if (flag) {
        this.getTableRecords();
      }
    },
    resetForm() {
      this.search = "";
      this.rows = [];
      this.selected = [];
      this.$refs.form3.reset();
    },
    resetValidation() {
      this.$refs.form2.resetValidation();
    },
    getTableRecords() {
      console.log("getTableRecords called");

      var YearCode = this.JciYearCode;
      console.log("YearCode=" + YearCode);

      this.rows = [];

      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/mission-onelakh/members-list";
      var upload = {
        UserInterface: 1,
      };
      console.log("upload=" + JSON.stringify(upload));
      console.log(
        "server_url=" + server_url + ", token=" + token + ", add_url=" + add_url
      );

      this.rows = [];
      this.SearchFlag = true;
      this.LoadingFlag = true;
      this.OverlayLoadingFlag = true;

      const thisIns = this;
      var output = "";
      var records = "";
      var flag = 0;

      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        onUploadProgress: function (progressEvent) {
          this.uploadPercentage = parseInt(
            Math.round((progressEvent.loaded / progressEvent.total) * 100)
          );
        }.bind(this),
      })
        .then(function (response) {
          console.log("response=" + JSON.stringify(response));

          thisIns.SearchFlag = false;
          thisIns.LoadingFlag = false;
          thisIns.StartupLoadingFlag = false;
          thisIns.OverlayLoadingFlag = false;

          output = response.data.output;
          flag = response.data.flag;
          records = response.data.records;
          console.log(
            "output=" +
              output +
              ", flag=" +
              flag +
              ", records=" +
              JSON.stringify(records)
          );

          var options = records.TableOptions;
          thisIns.TableOptions1 = options;

          if (flag == 1) {
            thisIns.TableColumns1 = records.TableHeader;
            thisIns.rows = records.TableData;

            thisIns.toast("success", output);
          } else {
            thisIns.toast("error", output);
          }
        })
        .catch(function (error) {
          console.log("error=" + error);
          thisIns.toast("error", error);
          thisIns.SearchFlag = false;
          thisIns.LoadingFlag = false;
          thisIns.StartupLoadingFlag = false;
        });
    },
    deleteData(tr) {
      console.log("deleteData called");
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      console.log({ tr });
      if (n1 > 0) {
        Swal.fire({
          title: "Are you sure?",
          text: "You won't be able to delete this!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: `Delete`,
          confirmButtonColor: "#3085d6",
          cancelButtonText: `Cancel`,
          cancelButtonColor: "#d33",
        }).then((result) => {
          if (result.isConfirmed) {
            this.deleteRecord(tr);
          } else {
            this.sweetAlert("error", "You cancelled the process", true);
          }
        });
      } else {
        var message = "Kindly select one member to delete";
        this.toast("error", message);
      }
    },
    deleteRecord(tr) {
      console.log("deleteRecord called");

      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      console.log({ tr });

      if (n1 > 0) {
        this.OverlayLoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/mission-onelakh/member-delete";
        var upload = {
          UserInterface: 1,
          Committee: tr.CommitteeId,
          Lom: tr.MolId,
          CommitteeMember: tr.CommitteeMemberId,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.OverlayLoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            records = response.data.records;
            console.log(
              "output=" + output + ", flag=" + flag + ", records=" + records
            );

            if (flag == 1) {
              thisIns.sweetAlert("success", output, true);
              thisIns.getTableRecords();
            } else {
              thisIns.sweetAlert("error", output, false);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.OverlayLoadingFlag = false;
          });
      } else {
        console.log("some errors occured");
        var message = "Kindly select one member to delete";
        this.sweetAlert("error", message, false);
      }
    },
    getMolSetting() {
      console.log("getMolSetting called");

      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/mission-onelakh/mol-setting-show";
      var upload = {
        UserInterface: 1,
      };
      console.log("upload=" + JSON.stringify(upload));
      console.log(
        "server_url=" + server_url + ", token=" + token + ", add_url=" + add_url
      );

      this.SearchFlag = true;
      this.LoadingFlag = true;

      const thisIns = this;
      var output = "";
      var records = "";
      var flag = 0;

      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        onUploadProgress: function (progressEvent) {
          this.uploadPercentage = parseInt(
            Math.round((progressEvent.loaded / progressEvent.total) * 100)
          );
        }.bind(this),
      })
        .then(function (response) {
          console.log("response=" + JSON.stringify(response));

          thisIns.SearchFlag = false;
          thisIns.LoadingFlag = false;

          output = response.data.output;
          flag = response.data.flag;
          records = response.data.records;
          console.log(
            "output=" + output + ", flag=" + flag + ", records=" + records
          );

          if (flag == 1) {
            thisIns.MolSettingValues = records;
          }
        })
        .catch(function (error) {
          console.log("error=" + error);
          thisIns.toast("error", error);
          thisIns.SearchFlag = false;
          thisIns.LoadingFlag = false;
        });
    },
    getDesignationColor(designation) {
      const colors = {
        Chairman: "#0097d7",
        "Vice Chairman": "#ff9800",
        Secretary: "#1db954",
        Treasurer: "#ff6b6b",
        Member: "#9c27b0"
      };
      return colors[designation] || "#757575";
    },
  },
  beforeMount() {
    this.refreshPageData();
  },
};
</script>

<style lang="scss">
$navy: #3a67b1;
$aqua: #0097d7;
$gold: #edbe38;
$text-off-black: #0a0f29;
$text-white: #ffffff;
$danger-color: #e74c3c;

@keyframes slideDown {
  from { opacity: 0; transform: translateY(-20px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes slideInUp {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes slideInLeft {
  from { opacity: 0; transform: translateX(-30px); }
  to { opacity: 1; transform: translateX(0); }
}

@keyframes float {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-12px); }
}

@keyframes rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes shimmer {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

#committee-members-list {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
  padding: 30px 15px;

  .header-card {
    background: linear-gradient(135deg, $navy 0%, $aqua 50%, $navy 100%) !important;
    border-radius: 20px;
    overflow: hidden;
    animation: slideDown 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
    position: relative;
    border: none;
    box-shadow: 0 20px 60px rgba(58, 103, 177, 0.5), 0 0 40px rgba(0, 151, 215, 0.3);

    &::before {
      content: "";
      position: absolute;
      top: 0; left: 0; right: 0; bottom: 0;
      background: linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.1) 50%, transparent 70%);
      animation: shimmer 3s infinite;
    }

    .header-gradient { position: relative; z-index: 1; }
    .header-backdrop-effect {
      position: absolute;
      top: -50%; right: -50%;
      width: 200%; height: 200%;
      background: radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%);
      animation: rotate 25s linear infinite;
    }
    .header-content { position: relative; z-index: 2; }
  }

  .header-icon-box {
    background: linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.15) 100%);
    backdrop-filter: blur(15px);
    border-radius: 18px;
    padding: 14px;
    box-shadow: 0 12px 40px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.4);
    animation: float 4s ease-in-out infinite;
    border: 2px solid rgba(255,255,255,0.3);
  }

  .header-title {
    font-size: 36px; font-weight: 900;
    text-shadow: 2px 2px 8px rgba(0,0,0,0.3), 0 0 20px rgba(0,151,215,0.3);
    animation: slideInLeft 0.8s ease-out;
    letter-spacing: 0.8px;
    color: $text-white;
    margin: 0; padding: 0;
  }

  .header-subtitle {
    font-size: 17px; opacity: 0.98;
    animation: slideInLeft 1s ease-out 0.1s backwards;
    letter-spacing: 0.4px;
    color: $text-white;
    font-weight: 500;
    margin: 6px 0 0 0;
  }

  .action-btn {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    font-weight: 900 !important;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    background: linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.2) 100%) !important;
    border: 3px solid rgba(255,255,255,0.5) !important;
    backdrop-filter: blur(10px);
    color: white !important;
    font-size: 12px !important;

    &:hover {
      transform: translateY(-6px) scale(1.08);
      box-shadow: 0 16px 32px rgba(0,0,0,0.4) !important;
      background: linear-gradient(135deg, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0.3) 100%) !important;
      border: 3px solid rgba(255,255,255,0.7) !important;
    }
  }

  .add-member-btn {
    background: linear-gradient(135deg, #4caf50 0%, #45a049 100%) !important;
    border: 3px solid #4caf50 !important;

    &:hover {
      background: linear-gradient(135deg, #45a049 0%, #3d8b40 100%) !important;
      border: 3px solid #3d8b40 !important;
      box-shadow: 0 16px 32px rgba(76, 175, 80, 0.5) !important;
    }
  }

  .info-card {
    border-radius: 16px !important;
    background: linear-gradient(135deg, #e8f4f8 0%, #f0f7ff 100%) !important;
    box-shadow: 0 8px 24px rgba(58,103,177,0.1) !important;
    border: 2px solid rgba(0,151,215,0.2) !important;
    animation: slideInUp 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.1s backwards;

    .info-item {
      display: flex;
      align-items: center;
      padding: 12px;
      background: rgba(255,255,255,0.6);
      border-radius: 8px;
      transition: all 0.3s ease;

      &:hover {
        background: rgba(255,255,255,0.9);
        box-shadow: 0 4px 12px rgba(0,0,0,0.1);
      }

      .info-label { font-size: 12px; font-weight: 700; color: #666; text-transform: uppercase; letter-spacing: 0.5px; display: block; margin-bottom: 4px; }
      .info-value { font-size: 18px; font-weight: 800; margin: 0; color: $navy; }
    }
  }

  .instructions-card {
    border-radius: 16px !important;
    background: linear-gradient(135deg, #fff5e6 0%, #fffaf0 100%) !important;
    box-shadow: 0 12px 32px rgba(237,190,56,0.15) !important;
    border: 2px solid rgba(237,190,56,0.2) !important;
    animation: slideInUp 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.15s backwards;

    .instruction-heading { font-size: 18px; font-weight: 700; color: $navy; display: flex; align-items: center; margin: 0 0 16px 0; text-transform: uppercase; letter-spacing: 0.5px; }

    .instruction-content {
      .instruction-paragraph {
        display: flex; align-items: flex-start; gap: 12px;
        margin: 12px 0; padding: 8px 0;
        font-size: 14px; line-height: 1.6; color: $text-off-black;

        .v-icon { flex-shrink: 0; margin-top: 2px; }
      }

      .requirement-box {
        display: flex; gap: 16px;
        background: rgba(255,152,0,0.08);
        border-left: 4px solid #ff9800;
        padding: 16px;
        border-radius: 8px;
        margin-top: 16px;

        .v-icon { flex-shrink: 0; }
        p { margin: 0; font-weight: 700; color: $navy; }
        ul { margin: 8px 0 0 0; padding-left: 20px; color: $text-off-black; li { margin: 4px 0; font-size: 13px; } }
      }
    }
  }

  .table-card {
    border-radius: 16px !important;
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%) !important;
    box-shadow: 0 16px 48px rgba(58,103,177,0.12) !important;
    border: none !important;
    animation: slideInUp 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s backwards;
  }

  .table-title { font-size: 18px; font-weight: 700; color: $navy; display: flex; align-items: center; gap: 8px; margin: 0; }

  .member-card {
    border-radius: 12px !important;
    border: none !important;
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) !important;
    overflow: hidden;
    position: relative;

    &:hover {
      transform: translateY(-12px);
      box-shadow: 0 20px 48px rgba(0,0,0,0.2) !important;

      .member-header { transform: scale(1.05); }
    }

    .member-header {
      background: linear-gradient(135deg, #0097d7 0%, #00b4e6 100%);
      padding: 24px 16px 16px;
      display: flex;
      justify-content: center;
      transition: all 0.3s ease;

      .v-avatar { border: 5px solid white; box-shadow: 0 8px 24px rgba(0,0,0,0.2); }
    }

    .member-info {
      padding: 20px 16px !important;
      text-align: center;

      .member-name { font-size: 18px; font-weight: 800; color: $text-off-black; margin: 12px 0 8px 0; line-height: 1.3; }
      .member-designation { margin: 12px 0; .v-chip { font-weight: 700 !important; letter-spacing: 0.3px !important; } }

      .member-details {
        margin-top: 16px;
        padding-top: 16px;
        border-top: 1px solid #e0e0e0;

        .detail-row {
          display: flex; align-items: center; justify-content: center; gap: 8px;
          margin: 8px 0;
          font-size: 13px; color: #666;

          .detail-text { word-break: break-all; font-weight: 600; }
        }
      }
    }

    .member-actions {
      padding: 16px !important;
      border-top: 1px solid #f0f0f0;
      justify-content: center;

      .v-btn { transition: all 0.3s ease; &:hover { transform: scale(1.05); } }
    }
  }

  .empty-state-card {
    border-radius: 16px !important;
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%) !important;
    box-shadow: 0 16px 48px rgba(58,103,177,0.12) !important;
    border: none !important;
    animation: slideInUp 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s backwards;
  }

  @media (max-width: 1200px) {
    padding: 24px 12px;
    .header-title { font-size: 32px; }
    .member-card .member-header { padding: 20px 12px 12px; }
  }

  @media (max-width: 960px) {
    padding: 20px 12px;
    .header-title { font-size: 28px; }
    .header-subtitle { font-size: 14px; }
    .header-icon-box { padding: 10px; }
    .table-title { font-size: 16px; }
    .member-card {
      .member-header {
        padding: 16px 10px 8px;
        .v-avatar { width: 100px !important; height: 100px !important; }
      }
      .member-info .member-name { font-size: 16px; }
    }
  }

  @media (max-width: 600px) {
    padding: 16px 10px;
    .header-card { margin-bottom: 16px; .header-content { padding: 12px !important; } }
    .header-title { font-size: 22px; }
    .header-subtitle { font-size: 13px; }
    .info-card, .instructions-card, .table-card { margin-bottom: 16px; }
    .member-card {
      margin-bottom: 12px;
      .member-header {
        padding: 12px 8px 4px;
        .v-avatar { width: 80px !important; height: 80px !important; }
      }
      .member-info {
        padding: 12px 10px !important;
        .member-name { font-size: 14px; margin: 8px 0 4px 0; }
        .member-details {
          margin-top: 10px;
          padding-top: 10px;
          .detail-row { font-size: 12px; gap: 6px; }
        }
      }
      .member-actions { padding: 12px !important; .v-btn { font-size: 11px !important; } }
    }
  }
}

.gap-2 { gap: 8px; }
</style>
